import React, { useState } from 'react'
import {
  getUrlWithProtocol,
  getFormattedOrganizationNumber,
  getFormatedPhone,
  getDomainFromUrl,
  IconPhone,
} from '@bbl-digital/snorre'
import {
  getBbl,
  getBblDisplayNames,
  isForkjopActive,
} from 'shared/store/bbl/selectors'
import { useSelector } from 'react-redux'
import { getActiveClient } from 'shared/store/clients/selectors'
import { Link } from 'react-router-dom'
import {
  Button,
  CountryFlag,
  IconMail,
  Image,
  Modal,
  Text,
} from '@bbl-digital/snorre-v2'
import { getLogoInverted } from 'shared/utils/theme'

const FooterLayout: React.FC = () => {
  const { data } = useSelector(getBbl)
  const activeClient = useSelector(getActiveClient)
  const [contactOpen, setContactOpen] = useState(false)
  const { portal } = useSelector(getBblDisplayNames)
  const hasPreemption = useSelector(isForkjopActive)

  const privacyUrl = data?.settings?.customPrivacyStatementUrl

  const showGeneralQuestions =
    data?.settings?.phoneNumberForGeneralQuestions ||
    data?.settings?.emailForGeneralQuestions

  const showPortalQuestions =
    data?.settings?.phoneNumberForPortalQuestions ||
    data?.settings?.emailForPortalQuestions

  const invoiceAddress = `${activeClient?.invoiceZip} ${activeClient?.invoiceCity}`
  const businessAddress = `${activeClient?.businessZip} ${activeClient?.businessCity}`

  const ContactModal = (
    <Modal open={contactOpen} onClose={() => setContactOpen(false)}>
      <Modal.Header>Fakturaadresse</Modal.Header>
      <div>
        {activeClient && (
          <>
            {activeClient.companyNumber && (
              <>
                <Text className="text-text-primary font-semibold">EHF:</Text>
                <Text>
                  Org.nr:{' '}
                  {getFormattedOrganizationNumber(activeClient.companyNumber)}
                </Text>
              </>
            )}
            {data.settings.emailInvoiceReception && (
              <div className="flex flex-col  mt-2">
                <Text className="font-semibold text-text-primary">E-post:</Text>
                <a
                  href={`mailto:${data.settings.emailInvoiceReception}`}
                  target="_blank"
                  className="text-text-primary underline flex items-center gap-1"
                >
                  <IconMail className="size-2" />
                  {data.settings.emailInvoiceReception}
                </a>
              </div>
            )}
            <Text className="text-text-primary font-semibold mt-2">
              Adresse:
            </Text>
            <Text>
              {activeClient.invoiceAddress || activeClient.businessAddress}
            </Text>
            <div className="flex gap-xs">
              <Text>
                {activeClient.invoiceAddress ? invoiceAddress : businessAddress}
              </Text>
              {activeClient.invoiceAddress && (
                <CountryFlag countryCode={activeClient.invoiceCountryCode} />
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  )

  return (
    <>
      {contactOpen && ContactModal}
      {data && (
        <div className="max-w-full bg-surface-portalen-footer">
          <footer className="flex flex-col justify-between w-auto px-4 pb-3 pt-6">
            {/* Layout for 2 rows */}
            <div className="flex flex-col md:flex-row gap-3 pb-4 lg:pb-0 lg:gap-1">
              {/* Logo */}
              <div className="flex max-h-fit">
                <Image
                  src={getLogoInverted(true)}
                  alt={`${data?.shortName || portal} logo`}
                  height="40px"
                />
              </div>

              {/* Seksjons-grid til høyre for logo */}
              <div className="flex-1 min-w-0 mb-2 lg:ml-[225px] pb-2">
                <div
                  className="
        grid gap-2
        grid-cols-1
        md:[grid-template-columns:repeat(2,minmax(0,250px))]
        xl:[grid-template-columns:repeat(auto-fit,minmax(0,1fr))]
      "
                >
                  <div className="flex flex-1 flex-col gap-0 lg:!gap-1 max-h-fit">
                    <Text className="text-surface-portalen-footer-text text-lg font-bold pb-1">
                      Nyttige lenker
                    </Text>
                    {hasPreemption && (
                      <a
                        href={`${window.config.urls.forkjopUrl}${window.config.bblCode}`}
                        target="_blank"
                        className="text-surface-portalen-footer-text underline"
                        data-tracking-name="Footer"
                        data-tracking-event="Footer click: Forkjøpsrett"
                      >
                        Boliger på forkjøpsrett
                      </a>
                    )}
                    {data.settings.bblUrl && (
                      <a
                        href={`${getUrlWithProtocol(data.settings.bblUrl)}`}
                        target="_blank"
                        className="text-surface-portalen-footer-text underline"
                        data-tracking-name="Footer"
                        data-tracking-event="Footer click: BBL url"
                      >
                        {getDomainFromUrl(data.settings.bblUrl)}
                      </a>
                    )}
                  </div>
                  {activeClient && (
                    <div className="flex flex-1 flex-col gap-0 lg:!gap-1 max-h-fit">
                      <Text className="text-surface-portalen-footer-text text-lg font-bold pb-1">
                        {activeClient?.clientName}
                      </Text>
                      {activeClient.companyNumber && (
                        <Text className="text-surface-portalen-footer-text">
                          {`Org.nr ${getFormattedOrganizationNumber(
                            activeClient.companyNumber
                          )}`}
                        </Text>
                      )}
                      {activeClient.businessAddress &&
                        data.settings.businessAddressPost && (
                          <>
                            <Text className="text-surface-portalen-footer-text">
                              {activeClient.businessAddress}
                            </Text>
                            <Text className="text-surface-portalen-footer-text">
                              {businessAddress}
                            </Text>
                          </>
                        )}
                      {(activeClient.invoiceAddress ||
                        activeClient.businessAddress) &&
                        data.settings.invoiceAddressPost && (
                          <Button
                            variant="nostyle"
                            className="text-surface-portalen-footer-text underline self-start"
                            onClick={() => setContactOpen(true)}
                            trackingName="Footer"
                            trackingEvent="Footer click: Fakturaadresse modal open"
                          >
                            Fakturaadresse
                          </Button>
                        )}
                    </div>
                  )}
                  {showGeneralQuestions && (
                    <div className="flex flex-1 flex-col gap-0 lg:!gap-1 max-h-fit">
                      <Text className="text-surface-portalen-footer-text text-lg font-bold pb-1">
                        Generelle spørsmål
                      </Text>
                      {data.settings.phoneNumberForGeneralQuestions && (
                        <a
                          href={`tel:${data.settings.phoneNumberForGeneralQuestions}`}
                          target="_blank"
                          className="text-surface-portalen-footer-text underline flex items-center gap-1"
                          data-tracking-name="Footer"
                          data-tracking-event="Footer click: Generelle spørsmål telefon"
                        >
                          <IconPhone color="currentColor" size="14px" />
                          {getFormatedPhone(
                            data.settings.phoneNumberForGeneralQuestions
                          )}
                        </a>
                      )}
                      {data.settings.emailForGeneralQuestions && (
                        <a
                          href={`mailto:${data.settings.emailForGeneralQuestions}`}
                          target="_blank"
                          className="text-surface-portalen-footer-text underline flex items-center gap-1"
                          data-tracking-name="Footer"
                          data-tracking-event="Footer click: Generelle spørsmål email"
                        >
                          <IconMail className="size-2 text-surface-portalen-footer-text" />
                          {data.settings.emailForGeneralQuestions}
                        </a>
                      )}
                    </div>
                  )}
                  {showPortalQuestions && (
                    <div className="flex flex-1 flex-col gap-0 lg:!gap-1 max-h-fit">
                      <Text className="text-surface-portalen-footer-text text-lg font-bold pb-1">
                        Spørsmål om {portal}
                      </Text>
                      {data.settings.phoneNumberForPortalQuestions && (
                        <a
                          href={`tel:${data.settings.phoneNumberForPortalQuestions}`}
                          target="_blank"
                          className="text-surface-portalen-footer-text underline flex items-center gap-1"
                          data-tracking-name="Footer"
                          data-tracking-event="Footer click: Spørsmål om portalen telefon"
                        >
                          <IconPhone color="currentColor" size="14px" />
                          {getFormatedPhone(
                            data.settings.phoneNumberForPortalQuestions
                          )}
                        </a>
                      )}
                      {data.settings.emailForPortalQuestions && (
                        <a
                          href={`mailto:${data.settings.emailForPortalQuestions}`}
                          target="_blank"
                          className="text-surface-portalen-footer-text underline flex items-center gap-1"
                          data-tracking-name="Footer"
                          data-tracking-event="Footer click: Spørsmål om portalen email"
                        >
                          <IconMail className="size-2" />
                          {data.settings.emailForPortalQuestions}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-between border-t border-surface-portalen-aside-icon pt-3 flex-col md:!flex-row">
              <Text className="text-surface-portalen-footer-text text-sm">
                © BBL Pivotal. Versjon 2.0.1
              </Text>
              {privacyUrl ? (
                <a
                  href={
                    privacyUrl ? getUrlWithProtocol(privacyUrl) : '/privacy'
                  }
                  target="_blank"
                  data-tracking-name="Footer"
                  data-tracking-event="Footer click: Privacy"
                  className="text-surface-portalen-footer-text underline"
                >
                  Personvern og informasjonskapsler
                </a>
              ) : (
                <Link
                  to={'/privacy'}
                  data-tracking-name="Footer"
                  data-tracking-event="Footer click: Privacy"
                  className="text-surface-portalen-footer-text underline"
                >
                  Personvern og informasjonskapsler
                </Link>
              )}
            </div>
          </footer>
        </div>
      )}
    </>
  )
}

export default FooterLayout
